import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IonModal,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonIcon,
} from "@ionic/react";
import {
  GoogleMap,
  useJsApiLoader,
  DirectionsRenderer,
  Polyline,
  Marker,
} from "@react-google-maps/api";
import { closeOutline } from "ionicons/icons";
import { environment } from "../environment/environment";
import { JeepneyPlanStep, LatLng } from "../utils/jeepneyPlanner";
import "../pages/MapModal.css";
import "./ChatRouteModal.css";

interface StepCardStyle extends React.CSSProperties {
  "--step-accent": string;
  "--step-accent-soft": string;
  "--step-accent-line": string;
  "--step-surface-top": string;
  "--step-surface-bottom": string;
}

export interface ChatJeepneyRoutePreview {
  originLabel: string;
  destinationLabel: string;
  steps: JeepneyPlanStep[];
  topOptions: string[];
  segmentA: LatLng[];
  segmentB: LatLng[];
  fullRouteA: LatLng[];
  fullRouteB: LatLng[];
  walkTo?: { origin: LatLng; dest: LatLng } | null;
  transfer?: { origin: LatLng; dest: LatLng } | null;
  walkFrom?: { origin: LatLng; dest: LatLng } | null;
}

interface Props {
  isOpen: boolean;
  route: ChatJeepneyRoutePreview | null;
  onClose: () => void;
}

const libraries: ("places")[] = ["places"];

const premiumMapStyles: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#d9edf1" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#3d5a61" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f4fbfd" }] },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#9ec8ce" }],
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "landscape.man_made",
    elementType: "geometry",
    stylers: [{ color: "#d8ece8" }],
  },
  {
    featureType: "landscape.natural",
    elementType: "geometry",
    stylers: [{ color: "#dcefe4" }],
  },
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [{ color: "#dcebe8" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#f8fcfd" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#bfd8de" }],
  },
  {
    featureType: "road.arterial",
    elementType: "labels.text.fill",
    stylers: [{ color: "#54727a" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#cfe4ea" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#a9c8cf" }],
  },
  {
    featureType: "transit",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#a9d8ea" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#447184" }],
  },
];

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  gestureHandling: "greedy" as const,
  mapTypeControl: false,
  fullscreenControl: false,
  streetViewControl: false,
  styles: premiumMapStyles,
};

const hexToRgba = (hex: string, alpha: number) => {
  const clean = hex.replace("#", "");
  const value = clean.length === 3
    ? clean.split("").map((char) => char + char).join("")
    : clean;

  const num = parseInt(value, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ChatRouteModal: React.FC<Props> = ({ isOpen, route, onClose }) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [walkToRoute, setWalkToRoute] = useState<google.maps.DirectionsResult | null>(null);
  const [transferRoute, setTransferRoute] = useState<google.maps.DirectionsResult | null>(null);
  const [walkFromRoute, setWalkFromRoute] = useState<google.maps.DirectionsResult | null>(null);
  const [isPresented, setIsPresented] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapRenderKey, setMapRenderKey] = useState(0);
  const recoveryAttemptedRef = useRef(false);
  const recoveryTimerRef = useRef<number | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: environment.GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const mapCenter = useMemo(() => {
    if (route?.segmentA?.[0]) return route.segmentA[0];
    if (route?.walkTo?.origin) return route.walkTo.origin;
    return { lat: 10.6765, lng: 122.9509 };
  }, [route]);

  useEffect(() => {
    if (!isOpen) {
      recoveryAttemptedRef.current = false;
      setIsMapReady(false);
      setMapRenderKey(0);
    }
  }, [isOpen, route]);

  const fitRouteBounds = (instance: google.maps.Map) => {
    if (!route) return;

    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;
    const extend = (point: LatLng) => {
      bounds.extend(point);
      hasBounds = true;
    };

    route.segmentA.forEach(extend);
    route.segmentB.forEach(extend);
    route.walkTo && [route.walkTo.origin, route.walkTo.dest].forEach(extend);
    route.transfer && [route.transfer.origin, route.transfer.dest].forEach(extend);
    route.walkFrom && [route.walkFrom.origin, route.walkFrom.dest].forEach(extend);

    [walkToRoute, transferRoute, walkFromRoute].forEach((walkRoute) => {
      walkRoute?.routes?.[0]?.overview_path?.forEach((point) => {
        bounds.extend(point);
        hasBounds = true;
      });
    });

    if (hasBounds) {
      instance.fitBounds(bounds, 72);
    }
  };

  useEffect(() => {
    if (!route || !isOpen || !isLoaded) {
      setWalkToRoute(null);
      setTransferRoute(null);
      setWalkFromRoute(null);
      return;
    }

    const service = new google.maps.DirectionsService();
    const buildWalk = (
      segment: { origin: LatLng; dest: LatLng } | null | undefined,
      setter: React.Dispatch<React.SetStateAction<google.maps.DirectionsResult | null>>
    ) => {
      if (!segment) {
        setter(null);
        return;
      }

      service.route(
        {
          origin: segment.origin,
          destination: segment.dest,
          travelMode: google.maps.TravelMode.WALKING,
        },
        (result, status) => {
          if (status === "OK" && result) {
            setter(result);
            return;
          }
          setter(null);
        }
      );
    };

    buildWalk(route.walkTo, setWalkToRoute);
    buildWalk(route.transfer, setTransferRoute);
    buildWalk(route.walkFrom, setWalkFromRoute);
  }, [isLoaded, isOpen, route]);

  useEffect(() => {
    if (!map || !route || !isPresented) return;

    const firstPass = window.setTimeout(() => {
      window.google?.maps?.event?.trigger(map, "resize");
      fitRouteBounds(map);
    }, 120);
    const secondPass = window.setTimeout(() => {
      window.google?.maps?.event?.trigger(map, "resize");
      fitRouteBounds(map);
    }, 420);

    return () => {
      window.clearTimeout(firstPass);
      window.clearTimeout(secondPass);
    };
  }, [isPresented, map, route, transferRoute, walkFromRoute, walkToRoute]);

  useEffect(() => () => {
    if (recoveryTimerRef.current) {
      window.clearTimeout(recoveryTimerRef.current);
    }
  }, []);

  if (!route) return null;

  return (
    <IonModal
      isOpen={isOpen}
      keepContentsMounted={true}
      onDidDismiss={onClose}
      onDidPresent={() => setIsPresented(true)}
      onWillDismiss={() => {
        setIsPresented(false);
        setIsMapReady(false);
        setMap(null);
      }}
      className="chat-route-modal"
    >
      <IonHeader className="map-modal-header">
        <IonToolbar>
          <IonTitle>Jeepney Route Map</IonTitle>
          <IonButton className="map-header-close-button" fill="clear" slot="end" onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        </IonToolbar>
      </IonHeader>

      <IonContent className="chat-route-content">
        {loadError ? (
          <div className="chat-route-status">Failed to load Google Maps.</div>
        ) : !isLoaded ? (
          <div className="chat-route-status">Loading route map...</div>
        ) : (
          <div className="chat-route-shell">
            <div className="chat-route-map">
              {isPresented && (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={mapCenter}
                  zoom={13}
                  key={`${route.originLabel}-${route.destinationLabel}-${mapRenderKey}`}
                  onLoad={(instance) => {
                    setMap(instance);
                    setIsMapReady(false);
                    if (recoveryTimerRef.current) {
                      window.clearTimeout(recoveryTimerRef.current);
                    }
                    let ticks = 0;
                    const id = window.setInterval(() => {
                      window.dispatchEvent(new Event("resize"));
                      if (++ticks >= 12) window.clearInterval(id);
                    }, 600);
                    window.google?.maps?.event?.addListenerOnce(instance, "tilesloaded", () => {
                      window.clearInterval(id);
                      if (recoveryTimerRef.current) {
                        window.clearTimeout(recoveryTimerRef.current);
                        recoveryTimerRef.current = null;
                      }
                      setIsMapReady(true);
                      fitRouteBounds(instance);
                    });
                    recoveryTimerRef.current = window.setTimeout(() => {
                      const paintedTileCount = instance
                        .getDiv()
                        ?.querySelectorAll("img, canvas").length ?? 0;

                      if (paintedTileCount === 0 && !recoveryAttemptedRef.current) {
                        recoveryAttemptedRef.current = true;
                        setIsMapReady(false);
                        setMap(null);
                        setMapRenderKey((current) => current + 1);
                        return;
                      }

                      setIsMapReady(true);
                      fitRouteBounds(instance);
                    }, 1800);
                  }}
                  options={mapOptions}
                >
                  {isMapReady && walkToRoute && (
                    <DirectionsRenderer
                      directions={walkToRoute}
                      options={{
                        polylineOptions: { strokeColor: "#093843", strokeWeight: 3 },
                        suppressMarkers: true,
                        preserveViewport: true,
                      }}
                    />
                  )}

                  {isMapReady && route.fullRouteA.length > 0 && (
                    <Polyline
                      path={route.fullRouteA}
                      options={{
                        strokeColor: "#43ae91",
                        strokeWeight: 2,
                        strokeOpacity: 0.45,
                        zIndex: 1,
                      }}
                    />
                  )}
                  {isMapReady && route.segmentA.length > 0 && (
                    <Polyline
                      path={route.segmentA}
                      options={{ strokeColor: "#43ae91", strokeWeight: 5, zIndex: 3 }}
                    />
                  )}

                  {isMapReady && transferRoute && (
                    <DirectionsRenderer
                      directions={transferRoute}
                      options={{
                        polylineOptions: { strokeColor: "#093843", strokeWeight: 3, strokeOpacity: 0.65 },
                        suppressMarkers: true,
                        preserveViewport: true,
                      }}
                    />
                  )}

                  {isMapReady && route.fullRouteB.length > 0 && (
                    <Polyline
                      path={route.fullRouteB}
                      options={{
                        strokeColor: "#ff4570",
                        strokeWeight: 2,
                        strokeOpacity: 0.22,
                        zIndex: 1,
                      }}
                    />
                  )}
                  {isMapReady && route.segmentB.length > 0 && (
                    <Polyline
                      path={route.segmentB}
                      options={{ strokeColor: "#ff4570", strokeWeight: 5, zIndex: 3 }}
                    />
                  )}

                  {isMapReady && walkFromRoute && (
                    <DirectionsRenderer
                      directions={walkFromRoute}
                      options={{
                        polylineOptions: { strokeColor: "#093843", strokeWeight: 3 },
                        suppressMarkers: true,
                        preserveViewport: true,
                      }}
                    />
                  )}

                  {isMapReady && route.walkTo?.origin && (
                    <Marker
                      position={route.walkTo.origin}
                      label={{ text: "A", fontSize: "14px", fontWeight: "bold", color: "#ffffff" }}
                    />
                  )}
                  {isMapReady && route.segmentA.length > 0 && (
                    <Marker
                      position={route.segmentA[route.segmentA.length - 1]}
                      label={{ text: "B", fontSize: "14px", fontWeight: "bold", color: "#ffffff" }}
                    />
                  )}
                  {isMapReady && route.transfer?.dest && (
                    <Marker
                      position={route.transfer.dest}
                      label={{ text: "C", fontSize: "14px", fontWeight: "bold", color: "#ffffff" }}
                    />
                  )}
                  {isMapReady && route.segmentB.length > 0 && (
                    <Marker
                      position={route.segmentB[route.segmentB.length - 1]}
                      label={{ text: "D", fontSize: "14px", fontWeight: "bold", color: "#ffffff" }}
                    />
                  )}
                  {isMapReady && route.walkFrom?.dest && (
                    <Marker
                      position={route.walkFrom.dest}
                      label={{ text: route.segmentB.length > 0 ? "E" : "C", fontSize: "14px", fontWeight: "bold", color: "#ffffff" }}
                    />
                  )}
                </GoogleMap>
              )}
            </div>

            <div className="chat-route-panel">
              <div className="chat-route-header-card">
                <span className="chat-route-eyebrow">Bacolod commute</span>
                <h3>{route.originLabel} to {route.destinationLabel}</h3>
                <p>Recommended Bacolod jeepney route with the same local planner used in Navigate.</p>
              </div>

              <div className="chat-route-steps">
                {route.steps.map((step, index) => (
                  <div
                    className="chat-route-step-card"
                    key={`${step.mainLabel}-${step.label}-${index}`}
                    style={{
                      "--step-accent": step.color,
                      "--step-accent-soft": hexToRgba(step.color, 0.34),
                      "--step-accent-line": hexToRgba(step.color, 0.42),
                      "--step-surface-top": hexToRgba(step.color, 0.56),
                      "--step-surface-bottom": hexToRgba(step.color, 0.4),
                    } as StepCardStyle}
                  >
                    <span className="chat-route-step-number">{index + 1}</span>
                    <span className="chat-route-step-label">
                      <span className="chat-route-step-main">{step.mainLabel}</span>
                      {step.label}
                    </span>
                    <span className="chat-route-step-distance">{step.distance}</span>
                  </div>
                ))}
              </div>

              {route.topOptions.length > 0 && (
                <div className="chat-route-alt-card">
                  <span className="chat-route-alt-eyebrow">{route.topOptions.length} alternate options</span>
                  <h4>Other jeepney options</h4>
                  <div className="chat-route-alt-list">
                    {route.topOptions.map((option, index) => (
                      <div className="chat-route-alt-item" key={`${option}-${index}`}>
                        <span className="chat-route-alt-number">{index + 1}</span>
                        <span className="chat-route-alt-text">{option}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default ChatRouteModal;
